/**
 * Method Selection Component
 * Handles valuation method selection and method-specific configuration
 */
import React from 'react';
import PropTypes from 'prop-types';
import { HelpCircle } from 'lucide-react';

const MethodSelection = ({
  selectedParent,
  formData,
  onInputChange,
  onShowMethodModal,
  industries
}) => {
  // Get selected industry details for displaying benchmarks
  const selectedIndustry = industries.find(ind => ind.id === parseInt(formData.industry_id));

  return (
    <div className="mb-6 p-4 bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-lg">
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-semibold text-gray-800">
          Valuation Method
        </label>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            onShowMethodModal(true);
          }}
          className="text-blue-600 text-sm hover:underline flex items-center gap-1"
        >
          <HelpCircle size={14} />
          Learn about methods
        </button>
      </div>
      <select
        name="method"
        value={formData.method}
        onChange={onInputChange}
        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      >
        {selectedParent !== 'MANUAL' && <option value="cca">CCA (Comparable Company Analysis)</option>}
        <option value="dcf">DCF (Discounted Cash Flow)</option>
        <option value="capitalization">Capitalization of Earnings</option>
        <option value="nav">NAV (Net Asset Value)</option>
        <option value="manual">Manual Multiple</option>
      </select>

      {/* CCA Multiple Selection */}
      {formData.method === 'cca' && (
        <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h4 className="font-medium text-gray-900 mb-3">Select Primary Multiple for CCA</h4>
          <div>
            <select
              name="cca_multiple_type"
              value={formData.cca_multiple_type || 'ev_ebitda'}
              onChange={onInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="ev_ebitda">EV/EBITDA (Recommended)</option>
              <option value="ev_sales">EV/Revenue</option>
            </select>
          </div>
          {selectedIndustry && (
            <div className="mt-3 pt-3 border-t border-blue-200">
              <p className="text-xs font-medium text-gray-700 mb-1">Industry Benchmark for Selected Multiple:</p>
              <p className="text-sm text-blue-700 font-semibold">
                {selectedIndustry.industry_name}: {
                  formData.cca_multiple_type === 'ev_ebitda' || !formData.cca_multiple_type
                    ? `${selectedIndustry.multiples?.ev_ebitda?.median || 'N/A'}x (EV/EBITDA)`
                    : formData.cca_multiple_type === 'ev_sales'
                    ? `${selectedIndustry.multiples?.ev_sales?.median || 'N/A'}x (EV/Revenue)`
                    : 'N/A'
                }
              </p>
            </div>
          )}
        </div>
      )}

      {/* Manual Multiple Fields */}
      {formData.method === 'manual' && (
        <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h4 className="font-medium text-gray-900 mb-3">Manual Multiple Configuration</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Multiple Type
              </label>
              <select
                name="manual_multiple_type"
                value={formData.manual_multiple_type}
                onChange={onInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="ev_ebitda">EV/EBITDA</option>
                <option value="ev_sales">EV/Revenue</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Multiple Value
              </label>
              <input
                type="number"
                name="manual_multiple"
                value={formData.manual_multiple}
                onChange={onInputChange}
                step="0.1"
                placeholder="e.g., 5.0"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <p className="text-xs text-gray-600 mt-2">
            <strong>Tip:</strong> {
              formData.manual_multiple_type === 'ev_ebitda'
                ? 'Common EV/EBITDA multiples range from 4x-8x for small businesses, 8x-12x for mid-market.'
                : 'Common EV/Revenue multiples range from 0.5x-2x for most industries, 2x-10x+ for SaaS/tech with high margins.'
            }
          </p>
          {selectedIndustry && (
            <div className="mt-3 pt-3 border-t border-blue-200">
              <p className="text-xs font-medium text-gray-700 mb-1">Industry Benchmark:</p>
              <p className="text-sm text-blue-700 font-semibold">
                {selectedIndustry.industry_name}: {
                  formData.manual_multiple_type === 'ev_ebitda' && selectedIndustry.multiples?.ev_ebitda?.median
                    ? `${selectedIndustry.multiples.ev_ebitda.median}x`
                    : formData.manual_multiple_type === 'ev_sales' && selectedIndustry.multiples?.ev_sales?.median
                    ? `${selectedIndustry.multiples.ev_sales.median}x`
                    : 'N/A'
                }
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

MethodSelection.propTypes = {
  selectedParent: PropTypes.string.isRequired,
  formData: PropTypes.object.isRequired,
  onInputChange: PropTypes.func.isRequired,
  onShowMethodModal: PropTypes.func.isRequired,
  industries: PropTypes.array.isRequired
};

export default MethodSelection;
